import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Calendar, AlertTriangle } from 'lucide-react';

const AgeVerification = ({ onVerified }) => {
  const [showGate, setShowGate] = useState(false);
  const [birthYear, setBirthYear] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already verified
    const verified = localStorage.getItem('age_verified');
    if (!verified) {
      setShowGate(true);
    } else {
      onVerified?.();
    }
  }, [onVerified]);

  const handleVerify = () => {
    const year = parseInt(birthYear);
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;

    if (!year || year < 1900 || year > currentYear) {
      setError('Please enter a valid birth year');
      return;
    }

    if (age < 13) {
      setError('You must be 13 or older to use Pulsate');
      return;
    }

    // Store verification with timestamp
    localStorage.setItem('age_verified', JSON.stringify({
      verified: true,
      timestamp: Date.now(),
      ageGroup: age >= 18 ? 'adult' : 'teen'
    }));

    setShowGate(false);
    onVerified?.();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  if (!showGate) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-[#030305] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-verification-title"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-sm bg-[#0A0A0F] border border-white/10 rounded-3xl p-6 text-center"
        >
          {/* Shield Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#00F0FF]" />
          </div>

          {/* Title */}
          <h2 id="age-verification-title" className="text-xl font-bold text-white mb-2">
            Age Verification
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Pulsate is designed for users aged 13 and older. Please confirm your age to continue.
          </p>

          {/* Birth Year Input */}
          <div className="mb-4">
            <label htmlFor="birth-year" className="block text-left text-sm text-gray-400 mb-2">
              Enter your birth year
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                id="birth-year"
                type="number"
                value={birthYear}
                onChange={(e) => {
                  setBirthYear(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 2000"
                min="1900"
                max={new Date().getFullYear()}
                className="w-full pl-11 pr-4 py-3 bg-[#0F0F16] border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-[#00F0FF] transition-colors"
                aria-describedby={error ? "age-error" : undefined}
                data-testid="birth-year-input"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              id="age-error"
              className="flex items-center gap-2 p-3 mb-4 bg-[#FF2A2A]/10 border border-[#FF2A2A]/20 rounded-xl text-left"
              role="alert"
            >
              <AlertTriangle className="w-5 h-5 text-[#FF2A2A] flex-shrink-0" />
              <span className="text-sm text-[#FF2A2A]">{error}</span>
            </motion.div>
          )}

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            className="w-full py-3 bg-gradient-to-r from-[#00F0FF] to-[#00C8FF] text-black font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all touch-target"
            data-testid="verify-age-btn"
          >
            Verify Age
          </button>

          {/* Legal Text */}
          <p className="mt-4 text-xs text-gray-500">
            By continuing, you confirm you meet the minimum age requirement and agree to our{' '}
            <a href="/terms" className="text-[#00F0FF] hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</a>.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgeVerification;
